//@ts-nocheck

const COLLECTION_PRODUCTS_QUERY = `
query getCollectionProducts($handle: String!, $first: Int!, $after: String) {
  collection(handle: $handle) {
    products(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          handle
          availableForSale
          featuredImage {
            url(transform: { maxWidth: 500 })
            altText
            width
            height
          }
          media(first: 5) {
            edges {
              node {
                ... on MediaImage {
                  previewImage {
                    url(transform: { maxWidth: 500 })
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
          priceRange {
            minVariantPrice { amount currencyCode }
          }
          compareAtPriceRange {
            minVariantPrice { amount currencyCode }
          }
          variants(first: 20) {
            edges {
              node {
                id
                availableForSale
                quantityAvailable
                requiresShipping
                edition: metafield(namespace: "custom", key: "edition") { value }
                text: metafield(namespace: "custom", key: "text") { value }
                variantTitle: metafield(namespace: "custom", key: "variant_title") { value }
                flyVariantIds: metafield(namespace: "custom", key: "fly_config_data_product_variant_ids") { value }
                flyOfferId: metafield(namespace: "custom", key: "fly_gfb_offer_id") { value }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                image {
                  url(transform: { maxWidth: 500 })
                  altText
                  width
                  height
                }
              }
            }
          }
          tags
          kidYearTag: metafield(namespace: "custom", key: "kid_s_year_tag") { value }
          flyChildDigitalMap: metafield(namespace: "custom", key: "fly_child_digital_map") { value }
          linkedProducts: metafield(namespace: "custom", key: "linked_products") { value }
          relatedSuperbundle: metafield(namespace: "custom", key: "related_superbundle") { value }
          productRibbons: metafield(namespace: "custom", key: "product_ribbons") {
            reference {
              ... on Metaobject { fields { key value } }
            }
          }
          parentName: metafield(namespace: "custom", key: "parent_name") { value }
          subCatName: metafield(namespace: "custom", key: "sub_cat_name") { value }
          subSubCatName: metafield(namespace: "custom", key: "sub_sub_cat_name") { value }
          rating: metafield(namespace: "reviews", key: "rating") { value }
          ratingCount: metafield(namespace: "reviews", key: "rating_count") { value }
          judgemeBadge: metafield(namespace: "judgeme", key: "badge") { value }
        }
      }
    }
  }
}`;

const CACHE_KEY_PREFIX = "customProductList_v7_";
const CACHE_TTL_MS = 10 * 60 * 1000;

class CustomProductList extends HTMLElement {
  #abortController = new AbortController();
  #productJsonCache = {};
  #hasIntersected = false;
  #swiper = null;

  connectedCallback() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.#boot(), {
        once: true,
        signal: this.#abortController.signal,
      });
    } else {
      this.#boot();
    }
  }

  disconnectedCallback() {
    this.#destroySwiper();
    this.#abortController.abort();
  }

  get #collectionHandle() {
    return this.dataset.collectionHandle || "";
  }

  get #limit() {
    return parseInt(this.dataset.limit || "8", 10);
  }

  get #sectionId() {
    return this.dataset.sectionId || "";
  }

  get #grid() {
    return this.querySelector('[ref="productGrid"]');
  }

  get #swiperEl() {
    return this.querySelector(".product-swiper");
  }

  #boot() {
    this.#observeSwiperInit();
    this.#runFetchWhenReady();
  }

  #runFetchWhenReady() {
    if (typeof CustomProductListCards === "undefined") {
      setTimeout(() => this.#runFetchWhenReady(), 50);
      return;
    }
    if (!this.#collectionHandle || !window.PW_STOREFRONT_TOKEN) return;
    this.#fetchProducts();
  }

  #getCacheKey() {
    return CACHE_KEY_PREFIX + this.#collectionHandle + "_" + this.#limit;
  }

  #getCachedResponse() {
    try {
      const raw = sessionStorage.getItem(this.#getCacheKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.t == null || parsed.result == null) return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.result;
    } catch (e) {
      return null;
    }
  }

  #setCachedResponse(result) {
    try {
      sessionStorage.setItem(this.#getCacheKey(), JSON.stringify({ t: Date.now(), result }));
    } catch (e) {}
  }

  #getProductJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (this.#productJsonCache[handle]) return Promise.resolve(this.#productJsonCache[handle]);
    return fetch("/products/" + handle + ".js")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) this.#productJsonCache[handle] = data;
        return data;
      })
      .catch(() => null);
  }

  #getProductEdges(result) {
    if (!result || result.errors) return null;
    return (
      result.data &&
      result.data.collection &&
      result.data.collection.products &&
      result.data.collection.products.edges
    );
  }

  async #fetchProducts() {
    const cached = this.#getCachedResponse();
    if (cached) {
      await this.#applyResult(cached).catch(() => {});
      return;
    }

    try {
      const result = await this.#fetchProductsFromApi();
      if (!result.errors) this.#setCachedResponse(result);
      await this.#applyResult(result);
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
  }

  async #fetchProductsFromApi() {
    const res = await fetch("/api/2025-01/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": window.PW_STOREFRONT_TOKEN || "",
      },
      body: JSON.stringify({
        query: COLLECTION_PRODUCTS_QUERY,
        variables: { handle: this.#collectionHandle, first: this.#limit },
      }),
      signal: this.#abortController.signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(res.status + " " + text.slice(0, 200));
    }

    return res.json();
  }

  async #applyResult(result) {
    const edges = this.#getProductEdges(result);
    const grid = this.#grid;
    if (edges === null || !grid) return;

    if (!edges.length) {
      grid.innerHTML = '<div class="swiper-slide"><p>No products found</p></div>';
      this.#initSwiper();
      return;
    }

    const html = await CustomProductListCards.buildProductCardsHTML(edges, {
      sectionId: this.#sectionId,
      quickAddColorScheme: this.dataset.quickAddColorScheme || "",
      productTransition: this.dataset.productTransition || "",
      templateSuffix: document.body.getAttribute("data-pw-template-suffix") || "",
      getProductJson: this.#getProductJson.bind(this),
    });

    if (!html) return;
    grid.innerHTML = html;
    this.#initSwiper();
    CustomProductListCards.ensureFlyBundleHandler();
    CustomProductListCards.initLazyCardGalleries(grid, { root: this.#swiperEl });
    CustomProductListCards.trackFirstImageLoadTime(grid);
  }

  #destroySwiper() {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    } else if (this.#swiperEl && this.#swiperEl.swiper) {
      this.#swiperEl.swiper.destroy(true, true);
    }
  }

  #createSwiper() {
    if (typeof Swiper === "undefined" || !this.#swiperEl) return;
    this.#swiper = new Swiper(this.#swiperEl, {
      slidesPerView: 2.2,
      spaceBetween: 12,
      loop: false,
      navigation: false,
      pagination: { el: this.querySelector(".swiper-pagination"), clickable: true },
      breakpoints: {
        360: { slidesPerView: 2.3, spaceBetween: 8 },
        767: { slidesPerView: 3, spaceBetween: 10 },
        1024: { slidesPerView: 4, spaceBetween: 15 },
        1280: { slidesPerView: 5, spaceBetween: 12 },
      },
    });
  }

  #initSwiper() {
    if (!this.#hasIntersected) return;
    this.#destroySwiper();
    this.#createSwiper();
  }

  #observeSwiperInit() {
    if (!("IntersectionObserver" in window)) {
      this.#hasIntersected = true;
      this.#initSwiper();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          this.#hasIntersected = true;
          this.#initSwiper();
          observer.disconnect();
          break;
        }
      },
      { root: null, rootMargin: "100px 0px", threshold: 0 }
    );

    observer.observe(this);
  }
}

if (!customElements.get("custom-product-list")) {
  customElements.define("custom-product-list", CustomProductList);
}
